import LazyImage from 'components/Image/LazyImage';
import { FrameImage } from 'components/Product/ProductImagesModal';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import QuantityInput from 'containers/QuantityInput';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import translations from 'translations/index';
import { CartItem, Product, TrackingAddToCartAddFromType } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import calculateVAT from 'utils/calculateVAT';
import formatMoney, { formatCurrency } from 'utils/formatMoney';
import Money from 'components/Money/Money';
import generateProductSlug from 'utils/generateProductSlug';
import utilsMessages from 'utils/messages';
import DeleteIcon from '../icons/deleteIconMobile.svg';
import noImage from '../icons/noIMG.svg';
import NoteIcon from '../icons/noteIcon';

const ProductItemWrapper = styled.div`
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  background: #fff;
  position: relative;
  margin-bottom: 8px;
`;

const ProductItemContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ProductItemImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProductItemImage = styled.div<any>`
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 4px;
  display: inline-block;
  height: 48px;
  width: 48px;
`;

const ProductInfoDetailContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #111827;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const ProductUom = styled.div`
  margin-top: 2px;
  font-size: 10px;
  line-height: 14px;
  color: #4b5563;
  font-weight: 400;
`;

const ProductPrice = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 18px;
  color: #111827;
  font-weight: 600;
`;

const ProductPriceAfterVat = styled.div`
  font-size: 10px;
  line-height: 14px;
  color: #4b5563;
  font-weight: 400;
`;

const ProductQuantityAndTotalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ProductNoteContainer = styled.div`
  margin-left: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
`;

const ProductNote = styled.div`
  flex: 1;
  word-break: break-all;
  font-size: 10px;
  line-height: 14px;
  color: #6b7280;
  font-weight: 400;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const ProductOriginalPrice = styled.span`
  font-size: 10px;
  line-height: 14px;
  color: #9ca3af;
  font-weight: 400;
  text-decoration: line-through;
  margin-left: 6px;
  letter-spacing: -0.2px;
`;

const TotalMoney = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #111827;
  font-weight: 500;
`;

const DeleteProductContainer = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  cursor: pointer;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover {
    color: inherit;
  }
`;

const ItemImage = styled(({ noImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 48px;
  height: 48px;
  object-fit: ${({ noImage }) => (noImage ? 'contain' : 'cover')};
  ${({ noImage }) => (noImage ? `opacity: 0.3;` : '')};
`;

interface CartItemMobileProps {
  cartItem: CartItem;
  lang: string;
  onRemoveProduct: (item: CartItem) => void;
  openNote?: () => void;
  itemDiscountBeforeVat: number;
}

const CartItemMobile = (props: CartItemMobileProps) => {
  const { cartItem, lang, itemDiscountBeforeVat, onRemoveProduct, openNote } = props;

  const { uomInfo } = useProductUomInfo(cartItem?.product as Product);

  const frameTagImage = lang === 'vi' ? cartItem?.product?.tagImageVn : cartItem?.product?.tagImageEn || '';

  const pdpUrl = `/products/${generateProductSlug(cartItem?.product?.name || '', cartItem?.product?.supplierInternalCode || '', lang)}`;

  const totalBeforeVAT = useMemo(() => {
    return (
      calculateVAT(
        cartItem?.product?.priceV2?.price || 0,
        cartItem?.product?.priceV2?.vat || 0,
        cartItem?.product?.priceV2?.vatIncluded || false,
      ).beforeVat * (cartItem?.quantity || 0)
    );
  }, [cartItem]);

  const totalAfterVat = useMemo(() => {
    const totalAfterDiscountExcludedVAT = totalBeforeVAT - itemDiscountBeforeVat;
    const totalVATValue = Math.round((totalAfterDiscountExcludedVAT * (cartItem?.product?.priceV2?.vat || 0)) / 100);
    return totalAfterDiscountExcludedVAT + totalVATValue;
  }, [totalBeforeVAT, itemDiscountBeforeVat, cartItem]);

  return (
    <ProductItemWrapper>
      <ProductItemContainer>
        <ProductItemImageWrapper>
          <ProductLink to={pdpUrl}>
            <ProductItemImage>
              <ItemImage
                noImage={!cartItem?.product?.imageUrl}
                src={cartItem?.product?.imageUrl || noImage}
                alt={cartItem?.product?.name || name}
              />
              {frameTagImage && <FrameImage src={frameTagImage} />}
            </ProductItemImage>
          </ProductLink>
        </ProductItemImageWrapper>
        <ProductInfoDetailContainer>
          <ProductLink to={pdpUrl}>
            <ProductName>{cartItem?.product?.name}</ProductName>
          </ProductLink>
          <ProductUom>{uomInfo}</ProductUom>
          <ProductPrice>
            <Money>
              {
                calculateVAT(
                  cartItem?.product?.priceV2?.price || 0,
                  cartItem?.product?.priceV2?.vat || 0,
                  !!cartItem?.product?.priceV2?.vatIncluded,
                ).beforeVat
              }
            </Money>
            {!!cartItem?.product?.priceV2?.originalPrice &&
              cartItem?.product?.priceV2?.originalPrice > cartItem?.product?.priceV2?.price && (
                <ProductOriginalPrice>
                  {formatMoney(
                    calculateVAT(
                      cartItem?.product?.priceV2?.originalPrice || 0,
                      cartItem?.product?.priceV2?.vat || 0,
                      !!cartItem?.product?.priceV2?.vatIncluded,
                    ).beforeVat,
                  )}
                </ProductOriginalPrice>
              )}
          </ProductPrice>
          <ProductPriceAfterVat>
            <FormattedMessage
              {...utilsMessages.priceIncludeVat}
              values={{
                price: formatCurrency(
                  calculateVAT(
                    cartItem?.product?.priceV2?.price || 0,
                    cartItem?.product?.priceV2?.vat || 0,
                    !!cartItem?.product?.priceV2?.vatIncluded,
                  ).afterVat,
                ),
              }}
            />
          </ProductPriceAfterVat>
        </ProductInfoDetailContainer>
        <ProductQuantityAndTotalWrapper>
          <QuantityInput
            product={cartItem?.product as Product}
            size="small"
            min={0.01}
            allowDecimal={allowDecimalQuantity(cartItem?.product?.uom)}
            addFrom={TrackingAddToCartAddFromType.CartProduct}
            isShowTotalMoney={false}
          />
          <TotalMoney>
            {translations(utilsMessages.finalTotal)}: <Money>{totalAfterVat}</Money>
          </TotalMoney>
        </ProductQuantityAndTotalWrapper>

        <DeleteProductContainer onClick={() => onRemoveProduct(cartItem)}>
          <SVGIcon src={DeleteIcon} renderAsImage={false} />
        </DeleteProductContainer>
      </ProductItemContainer>
      <ProductNoteContainer onClick={openNote}>
        <NoteIcon size={10} color="#6B7280" />{' '}
        <ProductNote>{cartItem?.product?.note || translations(utilsMessages.note)}</ProductNote>
      </ProductNoteContainer>
    </ProductItemWrapper>
  );
};

export default CartItemMobile;
